import { eventChannel } from 'redux-saga';
import {
  all, call, put, select, cancel, take, fork, takeEvery, takeLatest
} from 'redux-saga/effects';
import * as actions from 'react/api/actions/stopDetailsPopup';
import defaultFetch from 'react/api/sagas/fetch/defaultFetch.js';
import { Map } from 'immutable';

const getMarkerToAddToMap = (kind, latitude, longitude, iconClassName, id) => {
  const icon = L.divIcon({
    className: iconClassName,
    iconSize: 36,
    iconAnchor: [18, 18]
  });
  const marker = L.marker([latitude, longitude], {
    icon,
    draggable: false
  });
  marker.id = id;
  marker.kind = kind;

  return marker;
};

const KIND_LAYERS = {
  SIGNATURE_MARKER_LAYER: 'SIGNATURE_MARKER_LAYER',
  SIGNATURE_ACCURACY_LAYER: 'SIGNATURE_ACCURACY_LAYER',
  ARRIVAL_MARKER_LAYER: 'ARRIVAL_MARKER_LAYER',
  DEPARTURE_MARKER_LAYER: 'DEPARTURE_MARKER_LAYER',
  ARRIVAL_ACCURACY_LAYER: 'ARRIVAL_ACCURACY_LAYER',
  SIGNATURES_HEATMAP: 'SIGNATURES_HEATMAP',
  ARRIVALS_HEATMAP: 'ARRIVALS_HEATMAP'
};

function* getDataFromStopDetailsPopupState(routeId, stopId, props) {
  const { stopDetailsPopup } = yield select();
  const stopDetailState = stopDetailsPopup.getIn([String(routeId), String(stopId)]);
  return props.reduce((acc, prop) => {
    acc[prop] = stopDetailState.get(prop);
    return acc;
  }, {});
}

function* getMapAndStopFromState(routeId, stopId) {
  return yield getDataFromStopDetailsPopupState(routeId, stopId, ['map', 'stop']);
}

function getCoodinatesForHeatMap(heatMapData, entity, latFieldName, lngFieldName) {
  return (heatMapData || []).reduce((acc, curr) => {
    const item = curr[entity];
    const lat = item?.[latFieldName];
    const lng = item?.[lngFieldName];
    if (lat && lng) {
      const coords = [lat, lng];
      acc.push(coords);
    }
    return acc;
  }, []);
}

function* getStopDetailData(action, success, fail) {
  const { routeId, stopId } = action.payload;
  const url = `/Stop/${stopId}/Detail`;
  try {
    const data = yield call(defaultFetch, url, {}, 'GET');
    const { error } = data;
    if (!error && typeof data === 'object' && data.id) {
      yield put(success(routeId, stopId, data));
    } else {
      yield put(fail(routeId, stopId, error || data));
    }
  } catch (error) {
    yield put(fail(routeId, stopId, error));
  }
}

function* requestStopDetailsData(action) {
  yield getStopDetailData(
    action,
    actions.completeRequestStopDetailsPopupData,
    actions.failRequestStopDetailsPopupData
  );
}

function* updateStopDetailsPopupData(action) {
  yield getStopDetailData(
    action,
    actions.completeUpdateStopDetailsPopupData,
    actions.failUpdateStopDetailsPopupData
  );
}

function* requestSignature(action) {
  const { stopDetailsPopup } = yield select();
  const { routeId, stopId } = action.payload;
  const stopKey = stopDetailsPopup.getIn([String(routeId), String(stopId), 'stop', 'key']);
  const url = encodeURI(`Route/${routeId}/Stop/${stopKey}/Signature`);
  try {
    const data = yield call(defaultFetch, url);
    const { error, signatureImageAsBase64 } = data;
    if (!error && signatureImageAsBase64) {
      yield put(actions.completeRequestSignature(routeId, stopId, data));
    } else {
      yield put(actions.failRequestSignature(routeId, stopId, error));
    }
  } catch (error) {
    yield put(actions.failRequestSignature(routeId, stopId, error));
  }
}

function* requestArrivalHeatMap(action) {
  const { routeId, stopId } = action.payload;
  const { stopDetailsPopup } = yield select();
  const stop = stopDetailsPopup.getIn([String(routeId), String(stopId), 'stop'], Map({}));
  const url = encodeURI('StopView/restrictions?criteria={"filters":["id","stop.id","stop.location.id", "stop.arrivalLatitude", "stop.arrivalLongitude", "stop.creationDate"],"firstResult":0,"maxResults":15}');
  const locationId = stop.getIn(['location', 'id']);

  if(locationId) {
    const body = JSON.parse(`{
      "criteriaChain":[
        {
          "and":[
            {"attr":"stop.location.id","eq":${locationId}}
          ]
        }
      ],
      "sort":[{"attr":"stop.creationDate","type":"DESC"}]
    }`);
    try {
      const data = yield call(defaultFetch, url, body, 'POST');
      const { error } = data;
      if (!error && Array.isArray(data)) {
        yield put(actions.completeRequestArrivalHeatMap(routeId, stopId, data));
      } else {
        yield put(actions.failRequestArrivalHeatMap(routeId, stopId, error));
      }
    } catch (error) {
      yield put(actions.failRequestArrivalHeatMap(routeId, stopId, error));
    }
  }
}

function* requestSignatureHeatMap(action) {
  const { routeId, stopId } = action.payload;
  const { stopDetailsPopup } = yield select();
  const stop = stopDetailsPopup.getIn([String(routeId), String(stopId), 'stop'], Map({}));
  const url = encodeURI('StopView/restrictions?criteria={"filters":["id","stop.id","stop.location.id", "stopSignature.latitude", "stopSignature.longitude", "stopSignature.creationDate"], "firstResult":0,"maxResults":15}');
  const locationId = stop.getIn(['location', 'id']);
  if(locationId) {
    const body = JSON.parse(`{
      "criteriaChain":[
        {
          "and":[
            {"attr":"stop.location.id","eq":${locationId}}
          ]
        }
      ],
      "sort":[{"attr":"stopSignature.creationDate","type":"DESC"}]
    }`);
    try {
      const data = yield call(defaultFetch, url, body, 'POST');
      const { error } = data;
      if (!error && Array.isArray(data)) {
        yield put(actions.completeRequestSignatureHeatMap(routeId, stopId, data));
      } else {
        yield put(actions.failRequestSignatureHeatMap(routeId, stopId, error));
      }
    } catch (error) {
      yield put(actions.failRequestSignatureHeatMap(routeId, stopId, error));
    }
  }
}

function* addSignatureMarkerToMap(action) {
  const { routeId, stopId } = action.payload;
  const { stop, map } = yield getMapAndStopFromState(routeId, stopId);
  const signatureLat = stop.get('signatureLatitude');
  const signatureLng = stop.get('signatureLongitude');
  if (signatureLat && signatureLng) {
    const color = 'darkorange';
    const lineStartLatLng = [stop.get('latitude'), stop.get('longitude')];
    const lineEndLatLng = [signatureLat, signatureLng];
    map.addLayer(getMarkerToAddToMap(
      KIND_LAYERS.SIGNATURE_MARKER_LAYER, signatureLat, signatureLng, 'stop-details-signature-location-icon', stopId
    ));
    Greenmile.Map.Utility.drawLine(
      lineStartLatLng, lineEndLatLng, color, map, KIND_LAYERS.SIGNATURE_MARKER_LAYER
    );
  }
}

function* removeSignatureMarkerFromMap(action) {
  const { map } = yield getDataFromStopDetailsPopupState(action.payload.routeId, action.payload.stopId, ['map']);
  Greenmile.Map.Utility.removeLayerByKind(map, [KIND_LAYERS.SIGNATURE_MARKER_LAYER]);
}

function* addSignatureAccuracyToMap(action) {
  const { routeId, stopId } = action.payload;
  const { stop, map } = yield getMapAndStopFromState(routeId, stopId);
  const signatureAccuracy = stop.get('signatureAccuracy');
  if (signatureAccuracy) {
    const signatureLat = stop.get('signatureLatitude');
    const signatureLng = stop.get('signatureLongitude');
    const color = 'darkorange';
    Greenmile.Map.Utility.drawAccuracyArea(
      signatureLat, signatureLng, signatureAccuracy,
      color, map, KIND_LAYERS.SIGNATURE_ACCURACY_LAYER
    );
  }
}

function* removeSignatureAccuracyFromMap(action) {
  const { map } = yield getDataFromStopDetailsPopupState(action.payload.routeId, action.payload.stopId, ['map']);
  Greenmile.Map.Utility.removeLayerByKind(map, ['SIGNATURE_ACCURACY_LAYER']);
}

function* addArrivalMarkerToMap(action) {
  const { routeId, stopId } = action.payload;
  const { stop, map } = yield getMapAndStopFromState(routeId, stopId);
  const arrivalLat = stop.get('arrivalLatitude');
  const arrivalLng = stop.get('arrivalLongitude');
  if (arrivalLat && arrivalLng) {
    const color = '#A825EE';
    const polylineStartLatLng = [stop.get('latitude'), stop.get('longitude')];
    const polylineEndLatLng = [arrivalLat, arrivalLng];
    map.addLayer(getMarkerToAddToMap(KIND_LAYERS.ARRIVAL_MARKER_LAYER, arrivalLat, arrivalLng, 'stop-details-arrival-location-icon'));
    Greenmile.Map.Utility.drawLine(
      polylineStartLatLng, polylineEndLatLng, color, map, KIND_LAYERS.ARRIVAL_MARKER_LAYER
    );
  }
}

function* removeArrivalMarkerFromMap(action) {
  const { map } = yield getDataFromStopDetailsPopupState(action.payload.routeId, action.payload.stopId, ['map']);
  Greenmile.Map.Utility.removeLayerByKind(map, [KIND_LAYERS.ARRIVAL_MARKER_LAYER]);
}

function* addDepartureMarkerToMap(action) {
  const { routeId, stopId } = action.payload;
  const { stop, map } = yield getMapAndStopFromState(routeId, stopId);
  const departureLat = stop.get('departureLatitude');
  const departureLng = stop.get('departureLongitude');
  if (departureLat && departureLng) {
    const color = '#9AD104';
    const polylineStartLatLng = [stop.get('latitude'), stop.get('longitude')];
    const polylineEndLatLng = [departureLat, departureLng];
    map.addLayer(getMarkerToAddToMap(KIND_LAYERS.DEPARTURE_MARKER_LAYER, departureLat, departureLng, 'stop-details-departure-location-icon'));
    Greenmile.Map.Utility.drawLine(
      polylineStartLatLng, polylineEndLatLng, color, map, KIND_LAYERS.DEPARTURE_MARKER_LAYER
    );
  }
}

function* removeDepartureMarkerFromMap(action) {
  const { map } = yield getDataFromStopDetailsPopupState(action.payload.routeId, action.payload.stopId, ['map']);
  Greenmile.Map.Utility.removeLayerByKind(map, [KIND_LAYERS.DEPARTURE_MARKER_LAYER]);
}

function* addArrivalAccuracyToMap(action) {
  const { routeId, stopId } = action.payload;
  const { stop, map } = yield getMapAndStopFromState(routeId, stopId);
  const arrivalAccuracy = stop.get('arrivalAccuracy');
  const arrivalLat = stop.get('arrivalLatitude');
  const arrivalLng = stop.get('arrivalLongitude');
  if (arrivalAccuracy && arrivalLat && arrivalLng) {
    const color = '#A825EE';
    Greenmile.Map.Utility.drawAccuracyArea(
      arrivalLat,
      arrivalLng,
      arrivalAccuracy,
      color,
      map,
      KIND_LAYERS.ARRIVAL_ACCURACY_LAYER
    );
  }
}

function* removeArrivalAccuracyFromMap(action) {
  const { map } = yield getDataFromStopDetailsPopupState(action.payload.routeId, action.payload.stopId, ['map']);
  Greenmile.Map.Utility.removeLayerByKind(map, [KIND_LAYERS.ARRIVAL_ACCURACY_LAYER]);
}

function* addSignatureHeatMapToMap(action) {
  const { routeId, stopId, coordinates } = action.payload;
  const { map } = yield getDataFromStopDetailsPopupState(routeId, stopId, ['map']);
  const heatMapOptions = {
    blur: 50,
    radius: 20,
    gradient: {
      0.00: 'darkorange',
      0.25: 'goldenrod',
      0.50: 'orange',
      0.75: 'orangered',
      1.00: 'maroon'
    }
  };
  map.addLayer(Greenmile.Map.Utility.createHeatmapLayer(
    KIND_LAYERS.SIGNATURES_HEATMAP,
    stopId,
    coordinates,
    heatMapOptions
  ));
}

function* removeSignatureHeatMapFromMap(action) {
  const { map } = yield getDataFromStopDetailsPopupState(action.payload.routeId, action.payload.stopId, ['map']);
  Greenmile.Map.Utility.removeLayerByKind(map, [KIND_LAYERS.SIGNATURES_HEATMAP]);
}

function* addArrivalHeatMapToMap(action) {
  const { routeId, stopId, coordinates } = action.payload;
  const { map } = yield getDataFromStopDetailsPopupState(routeId, stopId, ['map']);
  const heatMapOptions = {
    blur: 50,
    radius: 20,
    gradient: {
      0.00: 'darkblue',
      0.25: 'darkorchid',
      0.50: 'magenta',
      0.75: 'indigo',
      1.00: 'midnightblue'
    }
  };
  map.addLayer(Greenmile.Map.Utility.createHeatmapLayer(
    KIND_LAYERS.ARRIVALS_HEATMAP,
    stopId,
    coordinates,
    heatMapOptions
  ));
}

function* removeArrivalHeatMapFromMap(action) {
  const { map } = yield getDataFromStopDetailsPopupState(action.payload.routeId, action.payload.stopId, ['map']);
  Greenmile.Map.Utility.removeLayerByKind(map, [KIND_LAYERS.ARRIVALS_HEATMAP]);
}

function* startRequestSignature(action) {
  const { routeId, stopId } = action.payload;
  const { stopDetailsPopup } = yield select();
  const hasSignature = stopDetailsPopup.getIn([String(routeId), String(stopId), 'stop', 'hasSignature']);
  const signatureIsAlreadyDownloaded = stopDetailsPopup.getIn(['signature', String(routeId), String(stopId), 'signatureIsAlreadyDownloaded']);
  if (hasSignature && !signatureIsAlreadyDownloaded) {
    yield put(actions.requestSignature(routeId, stopId));
  }
}

function* handleWatchRequestStopDetailsPopupDataCompleted(action) {
  yield call(startRequestSignature, action);
}

function* disableZones(action) {
  const { routeId, stopId } = action.payload;
  yield put(actions.disableZones(routeId, stopId));
}

function* disableHeatMaps(action) {
  const { routeId, stopId } = action.payload;
  yield put(actions.disableHeatMaps(routeId, stopId));
}

function* renderMarkerAndOpenPopup(routeId, stopId, event) {
  const { stopDetailsPopup } = yield select();
  const stop = stopDetailsPopup.getIn([routeId, stopId, 'stop']).toJS();
  const screen = Monet.Session.RouteDetail.screensList[routeId];
  if (Monet.Session.RouteDetail.screensList[routeId].editingStop) {
    const geocodeStop = new Greenmile.RouteDetail.GeocodeStop(routeId, screen.map);
    const position = event.latlng;
    Monet.Session.RouteDetail.screensList[routeId].editingStopPosition = position;
    Monet.Session.RouteDetail.screensList[routeId].isUpdateAutomatic = false;
    geocodeStop.setCorrectLocation(position.lat, position.lng)
      .setStop(stop)
      .renderMarkerAndOpenPopup();
  }
}

function mapEventsChannel(map) {
  const listener = eventChannel((emitter) => {
    map.on('click', (event) => {
      emitter(event);
    });
    return () => {
      map.off('click');
    };
  });
  return listener;
}

function* listenToMapClickEvent(routeId, stopId) {
  const screen = Monet.Session.RouteDetail.screensList[routeId];
  const channel = yield call(mapEventsChannel, screen.map);
  try {
    while (true) {
      const event = yield take(channel);
      window.GreenmileReduxStore.dispatch(actions.openPopup(routeId, stopId, event));
    }
  } finally {
    channel.close();
  }
}

function* handleGeocodeEnabled(routeId, stopId) {
  const { stopDetailsPopup } = yield select();
  const stop = stopDetailsPopup.getIn([routeId, stopId, 'stop']).toJS();
  const marker = stopDetailsPopup.getIn([routeId, stopId, 'marker']);
  const screen = Monet.Session.RouteDetail.screensList[routeId];
  const geocodeStop = new Greenmile.RouteDetail.GeocodeStop(routeId, screen.map);
  geocodeStop.setNewMarker(stop, marker, routeId);
  new Greenmile.RouteDetail.SuggestGeocodeStop(routeId, screen.map, screen)
    .setData(stop)
    .render()
    .getSuggestionAndRenderResult();
  const routeMapsId = `#routeMaps_${routeId}`;
  App.Views.RouteDetails.hideTabInformation(routeMapsId, routeId);
  marker.closePopup();
}

function* handleGeocodeCanceled(routeId, stopId) {
  const { stopDetailsPopup } = yield select();
  const stop = stopDetailsPopup.getIn([routeId, stopId, 'stop']).toJS();
  const marker = stopDetailsPopup.getIn([routeId, stopId, 'marker']);
  const screen = Monet.Session.RouteDetail.screensList[routeId];
  const geocodePopup = new Greenmile.RouteDetail.PopupToGeocode(routeId, marker, stop.id);
  const routeDetailGeocode = new Greenmile.RouteDetail.GeocodeStop(routeId, screen.map);
  geocodePopup.clickActionToCancel();
  routeDetailGeocode.setStop(stop).removeGeocodedPosition();
}


function* handleRequestArrivalHeatMapCompleted(action) {
  const { routeId, stopId, heatMapData } = action.payload;
  const { stopDetailsPopup } = yield select();
  const arrivalHeatMapIsEnabled = stopDetailsPopup.getIn(['actionButtons', String(routeId), String(stopId), 'arrivalHeatMapIsEnabled'], false);
  if (arrivalHeatMapIsEnabled) {
    const coordinates = yield call(getCoodinatesForHeatMap, heatMapData, 'stop', 'arrivalLatitude', 'arrivalLongitude');
    yield put(actions.addArrivalHeatMapToMap(routeId, stopId, coordinates));
  }
}

function* handleRequestSignatureHeatMapCompleted(action) {
  const { routeId, stopId, heatMapData } = action.payload;
  const { stopDetailsPopup } = yield select();
  const signatureHeatMapIsEnabled = stopDetailsPopup.getIn(['actionButtons', String(routeId), String(stopId), 'signatureHeatMapIsEnabled'], false);
  if (signatureHeatMapIsEnabled) {
    const coordinates = yield call(getCoodinatesForHeatMap, heatMapData, 'stopSignature', 'latitude', 'longitude');
    yield put(actions.addSignatureHeatMapToMap(routeId, stopId, coordinates));
  }
}

function* watchGeocode() {
  while (true) {
    const listeners = {};
    let action = yield take(actions.GEOCODE_ENABLED);
    const { routeId, stopId } = action.payload;
    if (!listeners[routeId]) {
      listeners[routeId] = {};
    }

    listeners[routeId][stopId] = yield fork(listenToMapClickEvent, routeId, stopId);

    yield call(handleGeocodeEnabled, routeId, stopId);

    while (true) {
      action = yield take([
        actions.GEOCODE_ENABLED,
        actions.GEOCODE_CANCELED,
        actions.GEOCODE_DISABLED,
        actions.GEOCODE_POPUP_OPENED
      ]);

      const { routeId, stopId, event } = action.payload;

      if (!listeners[routeId]) {
        listeners[routeId] = {};
      }

      if (listeners[routeId][stopId] && (
        action.type === actions.GEOCODE_ENABLED
        || action.type === actions.GEOCODE_CANCELED
        || action.type === actions.GEOCODE_DISABLED
      )) {
        yield cancel(listeners[routeId][stopId]);
      }

      if (action.type === actions.GEOCODE_ENABLED) {
        listeners[routeId][stopId] = yield fork(listenToMapClickEvent, routeId, stopId);
        yield call(handleGeocodeEnabled, routeId, stopId);
      }
      if (action.type === actions.GEOCODE_CANCELED) {
        yield call(handleGeocodeCanceled, routeId, stopId);
      }
      if (action.type === actions.GEOCODE_POPUP_OPENED) {
        yield call(renderMarkerAndOpenPopup, routeId, stopId, event);
      }
    }
  }
}

function* watchSignatureLocationAddedToMap() {
  yield takeEvery(actions.SIGNATURE_LOCATION_ADDED_TO_MAP, addSignatureMarkerToMap);
}

function* watchSignatureLocationRemovedFromMap() {
  yield takeEvery(actions.SIGNATURE_LOCATION_REMOVED_FROM_MAP, removeSignatureMarkerFromMap);
}

function* watchSignatureAccuracyAddedToMap() {
  yield takeEvery(actions.SIGNATURE_ACCURACY_ADDED_TO_MAP, addSignatureAccuracyToMap);
}

function* watchSignatureAccuracyRemovedFromMap() {
  yield takeEvery(actions.SIGNATURE_ACCURACY_REMOVED_FROM_MAP, removeSignatureAccuracyFromMap);
}

function* watchArrivalLocationAddedToMap() {
  yield takeEvery(actions.ARRIVAL_LOCATION_ADDED_TO_MAP, addArrivalMarkerToMap);
}

function* watchArrivalLocationRemovedFromMap() {
  yield takeEvery(actions.ARRIVAL_LOCATION_REMOVED_FROM_MAP, removeArrivalMarkerFromMap);
}

function* watchDepartureLocationAddedToMap() {
  yield takeEvery(actions.DEPARTURE_LOCATION_ADDED_TO_MAP, addDepartureMarkerToMap);
}

function* watchDepartureLocationRemovedFromMap() {
  yield takeEvery(actions.DEPARTURE_LOCATION_REMOVED_FROM_MAP, removeDepartureMarkerFromMap);
}

function* watchArrivalAccuracyAddedToMap() {
  yield takeEvery(actions.ARRIVAL_ACCURACY_ADDED_TO_MAP, addArrivalAccuracyToMap);
}

function* watchArrivalAccuracyRemovedFromMap() {
  yield takeEvery(actions.ARRIVAL_ACCURACY_REMOVED_FROM_MAP, removeArrivalAccuracyFromMap);
}

function* watchSignatureHeatMapAddedToMap() {
  yield takeEvery(actions.SIGNATURE_HEAT_MAP_ADDED_TO_MAP, addSignatureHeatMapToMap);
}

function* watchSignatureHeatMapRemovedFromMap() {
  yield takeEvery(actions.SIGNATURE_HEAT_MAP_REMOVED_FROM_MAP, removeSignatureHeatMapFromMap);
}

function* watchArrivalHeatMapAddedToMap() {
  yield takeEvery(actions.ARRIVAL_HEAT_MAP_ADDED_TO_MAP, addArrivalHeatMapToMap);
}

function* watchArrivalHeatMapRemovedFromMap() {
  yield takeEvery(actions.ARRIVAL_HEAT_MAP_REMOVED_FROM_MAP, removeArrivalHeatMapFromMap);
}

function* watchRequestSignatureHeatMapStarted() {
  yield takeEvery(actions.REQUEST_SIGNATURE_HEAT_MAP_STARTED, requestSignatureHeatMap);
}

function* watchRequestSignatureHeatMapCompleted() {
  yield takeEvery(
    actions.REQUEST_SIGNATURE_HEAT_MAP_COMPLETED,
    handleRequestSignatureHeatMapCompleted
  );
}

function* watchRequestArrivalHeatMapStarted() {
  yield takeEvery(actions.REQUEST_ARRIVAL_HEAT_MAP_STARTED, requestArrivalHeatMap);
}

function* watchRequestArrivalHeatMapCompleted() {
  yield takeEvery(actions.REQUEST_ARRIVAL_HEAT_MAP_COMPLETED, handleRequestArrivalHeatMapCompleted);
}

function* watchRequestSignatureStarted() {
  yield takeEvery(actions.REQUEST_SIGNATURE_STARTED, requestSignature);
}

function* watchRequestStopDetailsPopupDataStarted() {
  yield takeEvery(actions.REQUEST_STOP_DETAILS_POPUP_DATA_STARTED, requestStopDetailsData);
}

function* watchRequestStopDetailsPopupDataCompleted() {
  yield takeEvery(
    actions.REQUEST_STOP_DETAILS_POPUP_DATA_COMPLETED,
    handleWatchRequestStopDetailsPopupDataCompleted
  );
}

function* watchUpdateStopDetailsPopupData() {
  yield takeEvery(
    actions.REQUEST_UPDATE_STOP_DETAILS_POPUP_DATA_STARTED,
    updateStopDetailsPopupData
  );
}

function* watchZonesEnabled() {
  yield takeLatest(actions.ZONES_ENABLED, disableHeatMaps);
}

function* watchHeatMapsEnabled() {
  yield takeLatest(actions.HEATMAPS_ENABLED, disableZones);
}

export default function* () {
  yield all([
    watchRequestStopDetailsPopupDataStarted(),
    watchRequestStopDetailsPopupDataCompleted(),
    watchGeocode(),
    watchRequestSignatureStarted(),
    watchSignatureLocationAddedToMap(),
    watchSignatureLocationRemovedFromMap(),
    watchSignatureAccuracyAddedToMap(),
    watchSignatureAccuracyRemovedFromMap(),
    watchArrivalLocationAddedToMap(),
    watchArrivalLocationRemovedFromMap(),
    watchArrivalAccuracyAddedToMap(),
    watchArrivalAccuracyRemovedFromMap(),
    watchSignatureHeatMapAddedToMap(),
    watchSignatureHeatMapRemovedFromMap(),
    watchArrivalHeatMapAddedToMap(),
    watchArrivalHeatMapRemovedFromMap(),
    watchRequestSignatureHeatMapStarted(),
    watchRequestSignatureHeatMapCompleted(),
    watchRequestArrivalHeatMapStarted(),
    watchRequestArrivalHeatMapCompleted(),
    watchDepartureLocationAddedToMap(),
    watchDepartureLocationRemovedFromMap(),
    watchUpdateStopDetailsPopupData(),
    watchZonesEnabled(),
    watchHeatMapsEnabled()
  ]);
}
